// ────────────────────────────────────────────────────────────────────────────
// The admin accent palette — one definition, both brands.
//
// Before this file every admin page declared its own `BRAND_C`/`--brand`/
// `AX_ACCENT` constants, and they had drifted: the sidebar used Tailwind
// amber-500 (#F59E0B), Analytics used #F08A24, the Accounting hero used
// #F5A623, and "blue" was #2563EB in eight places but #1976D2 on the public
// site. Walking between two admin pages, the accent visibly changed hue.
//
//   Accounting  (connectcorporate.com) → yellow/gold, tied to --pub-gold
//   Consulting  (conncct.com)          → blue
//
// The CSS side of these tokens lives in styles/adminShell.css under
// `.adminx` / `.adminx.is-corp`; this module is for the places that need the
// value in JS (chart fills, inline SVG, gradients passed to style props).
// Keep the two in sync — same hexes, same names.
// ────────────────────────────────────────────────────────────────────────────
import { BRAND } from '@/brand';

export interface AdminAccent {
  /** Accent fill: primary buttons, active nav pill, badges. */
  brand: string;
  /** Accent fill on hover/press. */
  brandH: string;
  /** Text/icon colour to use ON TOP of `brand` — white on blue, dark ink on gold. */
  brandOn: string;
  /** Accent for text and icons on a white surface (dark enough to read). */
  brandD: string;
  /** Tinted background — soft chips, icon tiles, selected rows. */
  brandSoft: string;
  /** Border colour that pairs with `brandSoft`. */
  brandLine: string;
  /** Lighter accent, for the far end of avatar/badge gradients. */
  brandLt: string;
  /** Hero / banner gradient. */
  grad: string;
  /** Focus ring (accent at low alpha). */
  ring: string;
}

const CONSULTING: AdminAccent = {
  brand:     '#2563EB',
  brandH:    '#1D4ED8',
  brandOn:   '#FFFFFF',
  brandD:    '#1D4ED8',
  brandSoft: '#DBEAFE',
  brandLine: '#BFDBFE',
  brandLt:   '#60A5FA',
  grad:      'linear-gradient(135deg,#3B82F6 0%,#2563EB 46%,#1D4ED8 100%)',
  ring:      'rgba(37,99,235,.22)',
};

// Gold, not orange. White text on gold is unreadable (~2:1), so `brandOn` is
// dark ink and `brandD` is a darkened gold for accent text on white (~4.7:1).
const CORPORATE: AdminAccent = {
  brand:     '#F2B01E',
  brandH:    '#DDA015',
  brandOn:   '#231803',
  brandD:    '#A9740A',
  brandSoft: '#FEF4DA',
  brandLine: '#F6DFA6',
  brandLt:   '#FFD98A',
  grad:      'linear-gradient(135deg,#FFD54F 0%,#F2B01E 46%,#C98A06 100%)',
  ring:      'rgba(242,176,30,.28)',
};

export const IS_CORP = BRAND === 'corporate';

/** The accent for the brand this page load belongs to. */
export const AX: AdminAccent = IS_CORP ? CORPORATE : CONSULTING;

/** Both palettes, for the rare view that shows the two brands side by side. */
export const ADMIN_ACCENTS = { corporate: CORPORATE, consulting: CONSULTING } as const;

/** Neutrals — shared by both brands, mirrored in adminShell.css. */
export const AX_NEUTRAL = {
  ink:    '#0F172A',
  muted:  '#64748B',
  faint:  '#94A3B8',
  line:   '#E6E9ED',
  line2:  '#EFF1F4',
  bg:     '#F2F4FA',
  white:  '#FFFFFF',
} as const;

/** Label for the admin surface, used in the sidebar badge and page titles. */
export const AX_ADMIN_LABEL = IS_CORP ? 'Accounting Admin' : 'Consulting Admin';

/** Categorical series colours for admin charts, accent first. */
export const AX_SERIES: string[] = IS_CORP
  ? [CORPORATE.brand, '#0EA5A4', '#7C3AED', '#EF4444', '#64748B', '#2563EB']
  : [CONSULTING.brand, '#0EA5A4', '#F2B01E', '#EF4444', '#64748B', '#7C3AED'];
